// ============================================================
// Centralized API Client for Smplify Production Backend
// ============================================================

import { ErrorResponse, ResponseDTO } from '@/types/api';

// Base URL — update this to your production API domain
const API_BASE_URL = 'https://api.smplify.com';

// Token storage key
const TOKEN_KEY = 'smplify_auth_token';
const TENANT_KEY = 'smplify_tenant_id';

// --- Token management ---

export function setAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function setTenantId(tenantId: string) {
  localStorage.setItem(TENANT_KEY, tenantId);
}

export function getTenantId(): string | null {
  return localStorage.getItem(TENANT_KEY);
}

// --- API Error ---

export class ApiError extends Error {
  status: number;
  errorCode: string;
  path: string;

  constructor(err: ErrorResponse | Record<string, unknown>) {
    // The API returns errors in two shapes:
    //   1. Legacy flat:   { status, errorCode, message, path }
    //   2. Wrapped:       { success: false, path, error: { code, message, details }, timestamp }
    // Pull the message/code from whichever is present so callers don't get an
    // empty `Error.message` (which made Save look unresponsive — see issue
    // where users couldn't tell why the API rejected their request).
    const wrapped = (err as { error?: { code?: string; message?: string } }).error;
    const message =
      (err as ErrorResponse).message ||
      wrapped?.message ||
      "Request failed. Please try again.";
    const errorCode =
      (err as ErrorResponse).errorCode ||
      wrapped?.code ||
      "UNKNOWN";
    const status =
      typeof (err as ErrorResponse).status === "number"
        ? (err as ErrorResponse).status
        : 0;
    const path = ((err as ErrorResponse).path as string) || "";

    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.path = path;
  }
}

// --- Core request function ---

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  includeTenantHeader?: boolean;
  rawResponse?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, params, includeTenantHeader = false, rawResponse = false, ...init } = options;

  // Build URL with query params
  let url = `${API_BASE_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  // Build headers
  const headers: Record<string, string> = {
    'X-Partner': 'portal',
    ...(init.headers as Record<string, string>),
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (includeTenantHeader) {
    const tenantId = getTenantId();
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
  }

  // Set content type for JSON bodies
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    // Network-level failures: CORS blocks, no internet, DNS issues, etc.
    throw new Error(
      'Unable to connect to the server. This may be a network issue or a CORS restriction. Please check your internet connection and try again, or contact your administrator.'
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Handle binary responses
  if (rawResponse) {
    return response as unknown as T;
  }

  const json = await response.json();

  if (!response.ok) {
    throw new ApiError(json as ErrorResponse);
  }

  return json as T;
}

// --- Convenience methods ---

export async function apiGet<T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
  return request<T>(path, { ...options, method: 'GET' });
}

export async function apiPost<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'POST', body });
}

export async function apiPut<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'PUT', body });
}

export async function apiDelete<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return request<T>(path, { ...options, method: 'DELETE', body });
}

// --- Helper to unwrap ResponseDTO envelope ---

export function unwrap<T>(response: ResponseDTO<T>): T {
  if ('success' in response && !(response as any).success) {
    throw new Error((response as any).error?.message || 'Request failed');
  }
  return response.data;
}
